import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronRight, ChevronDown, ChevronsDownUp, ChevronsUpDown, Loader2, RotateCcw, Pencil, Copy, GitMerge, Trash2, Eye, AlertTriangle, Split, Combine, Archive } from 'lucide-react';
import { useSort } from '../hooks/useSort';
import { useTableColumnLayout, colCellStyle } from '../hooks/useTableColumnLayout';
import TableColHeader from '../components/TableColHeader';
import { TradeRail } from '../components/TradeRail';
import { canonicalUnit } from '../utils/measureUnits';
import { isPurgeCode, PURGE_LABEL } from '../utils/itemCodeGenerator';
import {
  BrioCard,
  BrioButton,
  BrioEmptyState,
  BrioInput,
  BrioModal,
  BrioModalBody,
  BrioModalField,
} from '../../components/BrioComponents';
import CreateLibraryItemModal from '../components/CreateLibraryItemModal';
import ReconcileItemModal from '../components/ReconcileItemModal';
import ItemEditorModal from '../components/ItemEditorModal';
import DuplicateItemModal from '../components/DuplicateItemModal';
import DeleteItemModal from '../components/DeleteItemModal';
import MergeItemsModal from '../components/MergeItemsModal';
import SplitItemModal from '../components/SplitItemModal';
import CombineVersionsModal from '../components/CombineVersionsModal';
import LibraryVersionsMenu, { archiveLabel } from '../components/LibraryVersionsMenu';
import { useBoqLibraryVersions } from '../hooks/useBoqLibraryVersions';
import { StickyScrollBox } from '../components/StickyScrollbar';

const fmtNum = (v) => (v == null ? null : Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 }));
// "01 Oct 2026, 14:32" — local time, 24 hour.
const fmtModified = (ms) => (ms == null ? null : new Date(ms).toLocaleString('en-GB', {
  day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
}));
const toMs = (iso) => {
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isNaN(t) ? null : t;
};
const UNTAGGED_KEY = '__untagged__';
// Purge: items moved out of their trade group while they are sorted. Not a
// taxonomy node, so it is keyed and labelled here and recognised by the
// OO.OO code prefix. Listed last, and only while it holds items.
const PURGE_KEY = '__purge__';
const PURGE_GROUP = { code: 'OO.OO', label: PURGE_LABEL, purge: true };
// Where an item files: its trade group, Purge, or Untagged. Real trade groups
// first in their curated order, then Untagged, then Purge.
const groupOf = (code, tradeGroupByItem) => {
  if (isPurgeCode(code)) return { key: PURGE_KEY, tg: PURGE_GROUP };
  const tg = tradeGroupByItem.get(code) || null;
  return { key: tg ? tg.code : UNTAGGED_KEY, tg };
};
const groupRank = (g) => (g.tg?.purge ? 2 : (g.tg ? 0 : 1));
const compareGroups = (a, b) => (groupRank(a) - groupRank(b)) || (a.tg && !a.tg.purge ? a.tg.sortOrder - b.tg.sortOrder : 0);
// Above this many search hits we don't auto-pull every match's rates/specs —
// rows load their detail on expand instead, keeping a broad search snappy.
const SEARCH_AUTOLOAD_LIMIT = 60;

// localStorage key for the resizable trade-selector rail width (see TradeRail).
const RAIL_WIDTH_KEY = 'boqLibraryRailWidth';

// Column layout (resize / collapse / freeze / order) — persisted via the hook.
const LIB_LAYOUT_KEY = 'qs-library-layout-v1';
const LIB_COLUMNS = [
  { id: 'expand', defaultWidth: 28, header: '', align: 'center', sortable: false, freezable: false, collapsible: false },
  { id: 'code', defaultWidth: 130, header: 'Code', sortField: 'code' },
  { id: 'description', defaultWidth: 360, header: 'Description (latest)', sortField: 'description' },
  { id: 'unit', defaultWidth: 90, header: 'Unit', sortField: 'unit' },
  { id: 'versions', defaultWidth: 96, header: 'Versions', sortField: 'versions', align: 'right' },
  { id: 'rate', defaultWidth: 120, header: 'Rate', sortField: 'rate', align: 'right' },
  { id: 'modified', defaultWidth: 150, header: 'Date modified', sortField: 'modified', align: 'right' },
  // minWidth so the row's buttons stay visible for anyone carrying a saved
  // column layout from before Duplicate was added.
  { id: 'actions', defaultWidth: 290, minWidth: 290, header: '', sortable: false },
  // Split / Combine, shown only on items holding more than one version.
  { id: 'versionActions', defaultWidth: 170, minWidth: 170, header: '', sortable: false },
];

const statusBadge = (status) => {
  if (!status || status === 'active') return null;
  const draft = status === 'draft';
  return (
    <span style={{ marginLeft: 6, fontSize: '0.66rem', textTransform: 'uppercase', letterSpacing: '0.03em', padding: '0 5px', borderRadius: 3, background: draft ? 'rgba(176,131,0,0.15)' : '#eee', color: draft ? '#8a6d00' : 'var(--brio-text-muted)' }}>
      {draft ? 'candidate' : status}
    </span>
  );
};

const specBlock = (label, value) => {
  if (!value) return null;
  return (
    <div style={{ marginTop: '0.25rem' }}>
      <span style={{ fontSize: '0.66rem', textTransform: 'uppercase', letterSpacing: '0.03em', color: 'var(--brio-text-muted)' }}>{label}: </span>
      <span style={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>{value}</span>
    </div>
  );
};

// Full spec for one version, shown when an item is expanded.
function VersionDetail({ version }) {
  const hasLong = version.longSpecGeneral || version.longSpecProducts || version.longSpecExecution;
  return (
    <div style={{ padding: '0.4rem 0', borderTop: '1px dashed var(--brio-border-subtle, #eee)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap' }}>
        <code style={{ color: 'var(--brio-text, #333)' }}>{version.fullCode}</code>
        <span style={{ fontWeight: 500 }}>{version.shortDescription}</span>
        {statusBadge(version.status)}
        <span style={{ color: 'var(--brio-text-muted)', fontSize: '0.78rem' }}>· {canonicalUnit(version.unit)}</span>
        {version.measurementRef && <span style={{ color: 'var(--brio-text-muted)', fontSize: '0.78rem', fontStyle: 'italic' }}>· {version.measurementRef}</span>}
      </div>
      {hasLong ? (
        <div style={{ paddingLeft: 6 }}>
          {specBlock('General', version.longSpecGeneral)}
          {specBlock('Products', version.longSpecProducts)}
          {specBlock('Execution', version.longSpecExecution)}
        </div>
      ) : (
        <div style={{ paddingLeft: 6, fontSize: '0.76rem', color: 'var(--brio-text-muted)', fontStyle: 'italic' }}>No long-form specification.</div>
      )}
      {version.changeReason && (
        <div style={{ paddingLeft: 6, fontSize: '0.72rem', color: 'var(--brio-text-muted)', marginTop: '0.2rem' }}>↳ {version.changeReason}</div>
      )}
    </div>
  );
}

// An archive carries every long spec and rate up front, so nothing loads
// lazily in view mode. Module-level so the effect dependency stays stable.
const NOOP_LOAD_DETAIL = () => {};

// Grey-out applied to every write control while an archive is on screen. The
// controls stay in place — same layout as the live page — but do nothing.
const readOnlyStyle = (readOnly) => (readOnly ? { opacity: 0.45, cursor: 'not-allowed' } : null);

const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '');

// Confirmation for "Make current". Spells out what a restore does to rows
// added since, because that part is not reversible by simply switching back.
function RestoreArchiveModal({ archive, busy, onClose, onConfirm }) {
  return (
    <BrioModal title="Make this version current" onClose={onClose} width="sm" footer={
      <>
        <BrioButton variant="secondary" onClick={onClose} disabled={busy}>Cancel</BrioButton>
        <BrioButton variant="primary" onClick={onConfirm} disabled={busy}>{busy ? 'Restoring…' : 'Make current'}</BrioButton>
      </>
    }>
      <BrioModalBody>
        <p style={{ marginTop: 0 }}>
          <strong>{archiveLabel(archive)}</strong> ({archive.code}) will replace the live library:
          {' '}{archive.itemCount} items, {archive.versionCount} versions, {archive.rateCount} rates.
        </p>
        <ul style={{ paddingLeft: '1.1rem', fontSize: '0.84rem', color: 'var(--brio-text-muted)' }}>
          <li>Items and versions in this archive are restored, and any edits made to them since are reverted.</li>
          <li>Anything added since is deleted, unless a project bill or a rate still points at it — those are marked deprecated instead, so no project BOQ breaks.</li>
          <li>The current library is archived automatically first, so this can be undone.</li>
        </ul>
      </BrioModalBody>
    </BrioModal>
  );
}

// Post-restore receipt: the RPC returns exactly what it changed, and a silent
// restore of ~5k rows is not something to leave unexplained.
function RestoreSummaryModal({ summary, onClose }) {
  const rows = [
    ['Items restored', summary.items_restored],
    ['Items deleted', summary.items_deleted],
    ['Items deprecated (still referenced)', summary.items_deprecated],
    ['Versions restored', summary.versions_restored],
    ['Versions reverted', summary.versions_reverted],
    ['Versions deleted', summary.versions_deleted],
    ['Versions deprecated (still referenced)', summary.versions_deprecated],
    ['Rates restored', summary.rates_restored],
    ['Rates reverted', summary.rates_reverted],
    ['Rates deleted', summary.rates_deleted],
    ['Classification tags rebuilt', summary.classifications],
  ];
  return (
    <BrioModal title={`Restored ${summary.archive_code}`} onClose={onClose} width="sm" footer={
      <BrioButton variant="primary" onClick={onClose}>Done</BrioButton>
    }>
      <BrioModalBody>
        <table style={{ width: '100%', fontSize: '0.84rem', borderCollapse: 'collapse' }}>
          <tbody>
            {rows.map(([label, n]) => (
              <tr key={label} style={{ borderBottom: '1px solid var(--brio-border-subtle, #eee)' }}>
                <td style={{ padding: '0.3rem 0', color: 'var(--brio-text-muted)' }}>{label}</td>
                <td style={{ padding: '0.3rem 0', textAlign: 'right', fontWeight: n ? 600 : 400 }}>{n}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p style={{ fontSize: '0.78rem', color: 'var(--brio-text-muted)', marginBottom: 0 }}>
          The library as it stood before this restore was archived first — pick the newest “Auto” version to go back.
        </p>
      </BrioModalBody>
    </BrioModal>
  );
}

function NewArchiveModal({ busy, onClose, onCreate }) {
  const [label, setLabel] = useState('');
  return (
    <BrioModal title="Save a library version" onClose={onClose} width="sm" footer={
      <>
        <BrioButton variant="secondary" onClick={onClose} disabled={busy}>Cancel</BrioButton>
        <BrioButton variant="primary" onClick={() => onCreate(label)} disabled={busy}>{busy ? 'Saving…' : 'Save version'}</BrioButton>
      </>
    }>
      <BrioModalBody form>
        <BrioModalField label="Note (optional)" hint="What is this copy for? e.g. before the 2026 rate update">
          <BrioInput value={label} onChange={(e) => setLabel(e.target.value)} autoFocus placeholder="e.g. before the 2026 rate update" />
        </BrioModalField>
      </BrioModalBody>
    </BrioModal>
  );
}

function LibraryPage({ library, onOpenRates, focusItemCode = null, onFocusConsumed }) {
  // Library archives: full frozen copies of the whole library. Selecting one
  // swaps the data source below and puts the page in read-only view mode.
  const archivesHook = useBoqLibraryVersions();
  const [viewing, setViewing] = useState(null);       // { archive, snapshot } once fully loaded
  const [switching, setSwitching] = useState(false);  // an archive is being pulled
  const [showNewArchive, setShowNewArchive] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [restoreSummary, setRestoreSummary] = useState(null);
  const [archiveError, setArchiveError] = useState(null);

  const readOnly = !!viewing;
  // Everything the table renders comes from whichever source is on screen; the
  // mutations always come from the live hook and are disabled while read-only.
  const source = viewing ? viewing.snapshot : library;
  const {
    items, versions, tradeGroups, tradeGroupByItem, rateAggByItem,
    brioSubtrades, brioSubtradeNodeByItem,
    tradeTypes, tradeTypeNodeByItem,
  } = source;
  const {
    createItemWithFirstVersion, duplicateItem, deleteItem, reconcileItem,
    nextItemCode, nextDuplicateCode, mergeItems,
    loadSplitCandidates, splitItem, combineVersions,
  } = library;
  // Re-tagging, editing and reconciling all need UPDATE / DELETE on the library,
  // which is admin/supervisor only. Duplicating and adding are INSERT-only, so
  // they stay open to everyone. Without this gate a plain user's re-tag would
  // half-apply: the DELETE silently matches nothing under RLS, the INSERT lands,
  // and the item ends up wearing two tags.
  const canWrite = library.canWrite;
  const loadGroupDetail = readOnly ? NOOP_LOAD_DETAIL : library.loadGroupDetail;
  const detailLoading = readOnly ? false : library.detailLoading;
  const loading = readOnly ? false : library.loading;
  // Live library only: an archive is a frozen copy, so "modified" means nothing there.
  const modifiedAtByItem = readOnly ? null : library.modifiedAtByItem;
  const error = readOnly ? null : library.error;

  const [showCreateItem, setShowCreateItem] = useState(false);
  const [reconcileTarget, setReconcileTarget] = useState(null);
  const [duplicateTarget, setDuplicateTarget] = useState(null);
  const [mergeTarget, setMergeTarget] = useState(null);       // row whose Merge was clicked
  // Not deleteTarget — that name is already taken by the archive-delete confirm.
  const [deleteItemTarget, setDeleteItemTarget] = useState(null);
  const [editorTarget, setEditorTarget] = useState(null);     // itemCode being edited
  const [splitTarget, setSplitTarget] = useState(null);       // itemCode whose Split was clicked
  const [combineTarget, setCombineTarget] = useState(null);   // itemCode whose Combine was clicked

  // Opening New item, Duplicate or Edit re-reads the taxonomy pickers, so a
  // range or trade type set on the Taxonomies page since this page loaded is
  // what the item code is generated from, rather than the copy read at page
  // load. Edit can renumber an item now, so it needs the same fresh copy.
  const { refreshPickers } = library;
  const pickersOpen = showCreateItem || !!duplicateTarget || !!editorTarget;
  useEffect(() => {
    if (pickersOpen) refreshPickers();
  }, [pickersOpen, refreshPickers]);

  // Trade types keyed by the brio sub-trade they are anchored to, used to name
  // an item's trade type on its row.
  const tradeTypesByAnchor = useMemo(() => {
    const byAnchor = new Map();
    (tradeTypes || []).forEach((t) => {
      if (!t.anchorId) return;
      if (!byAnchor.has(t.anchorId)) byAnchor.set(t.anchorId, []);
      byAnchor.get(t.anchorId).push({
        id: t.id,
        label: `${t.label}${t.shortCode ? ` · ${t.shortCode}` : ''}`,
      });
    });
    return byAnchor;
  }, [tradeTypes]);

  // Label for any trade type, used to keep a tag that no longer matches the
  // item's sub-trade visible rather than letting the select render blank.
  const tradeTypeLabelById = useMemo(
    () => new Map((tradeTypes || []).map((t) => [t.id, t.label])),
    [tradeTypes]
  );

  const tradeTypeOptionsFor = (subtradeNodeId, currentValue) => {
    const opts = (subtradeNodeId && tradeTypesByAnchor.get(subtradeNodeId)) || [];
    // An item reclassified since it was tagged would otherwise show an empty
    // box; surface the stale tag so it can be seen and cleared.
    if (currentValue && !opts.some((o) => o.id === currentValue)) {
      return [
        ...opts,
        { id: currentValue, label: `${tradeTypeLabelById.get(currentValue) || 'Trade type'} (other sub-trade)` },
      ];
    }
    return opts;
  };

  const [query, setQuery] = useState('');
  const [onlyCandidates, setOnlyCandidates] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);          // selected trade group (or UNTAGGED_KEY)
  const [expanded, setExpanded] = useState(() => new Set());      // item codes whose specs are shown
  const containerRef = useRef(null);
  // Rows ticked for Move to Purge. Cleared whenever the list on screen changes
  // to another trade group or search, so a tick can never act on rows out of view.
  const [picked, setPicked] = useState(() => new Set());
  const [purgeConfirm, setPurgeConfirm] = useState(false);
  const [purgeBusy, setPurgeBusy] = useState(false);
  const [purgeError, setPurgeError] = useState(null);
  const [purgeNotice, setPurgeNotice] = useState(null);

  const searching = query.trim().length > 0;
  useEffect(() => { setPicked(new Set()); setPurgeNotice(null); }, [selectedKey, query, onlyCandidates, viewing]);

  // ── Archive actions ────────────────────────────────────────────────────────
  const openArchive = async (archive) => {
    setArchiveError(null);
    setSwitching(true);
    const res = await archivesHook.fetchSnapshot(archive.id);
    setSwitching(false);
    if (!res.success) { setArchiveError(res.error); return; }
    // Selections are per-source: a trade the archive doesn't have would leave
    // the pane empty, so reset to the auto-select path.
    setSelectedKey(null);
    setExpanded(new Set());
    setViewing({ archive, snapshot: res.snapshot });
  };

  const returnToCurrent = () => {
    setViewing(null);
    setSelectedKey(null);
    setExpanded(new Set());
    setArchiveError(null);
  };

  const createArchive = async (label) => {
    setArchiveError(null);
    const res = await archivesHook.createArchive(label);
    if (!res.success) { setArchiveError(res.error); return; }
    setShowNewArchive(false);
  };

  const confirmDeleteArchive = async () => {
    const target = deleteTarget;
    setDeleteTarget(null);
    const res = await archivesHook.deleteArchive(target.id);
    if (!res.success) { setArchiveError(res.error); return; }
    if (viewing?.archive.id === target.id) returnToCurrent();
  };

  const confirmRestore = async () => {
    const target = restoreTarget;
    const res = await archivesHook.restoreArchive(target.id);
    if (!res.success) { setArchiveError(res.error); return; }
    setRestoreTarget(null);
    returnToCurrent();
    await library.refresh();
    setRestoreSummary(res.summary);
  };

  // Refresh follows whatever is on screen: the live library, or a re-pull of
  // the archive being viewed.
  const refresh = () => (viewing ? openArchive(viewing.archive) : library.refresh());

  const versionsByItem = useMemo(() => {
    const map = new Map();
    versions.forEach((v) => {
      if (!map.has(v.itemCode)) map.set(v.itemCode, []);
      map.get(v.itemCode).push(v);
    });
    return map;
  }, [versions]);

  // Every live item with the line the Merge picker shows for it.
  const mergeCandidates = useMemo(() => (mergeTarget ? items.map((i) => {
    const vers = versionsByItem.get(i.itemCode) || [];
    const active = vers.filter((v) => v.status === 'active');
    const latest = active[active.length - 1] || vers[vers.length - 1];
    return { itemCode: i.itemCode, description: latest?.shortDescription || '', versionCount: vers.length };
  }) : []), [mergeTarget, items, versionsByItem]);

  // Trade-group key for an item ('__untagged__' when not classified).
  const keyOfItem = useMemo(() => (code) => groupOf(code, tradeGroupByItem).key, [tradeGroupByItem]);

  // Lightweight selector summary: one row per trade group with item +
  // candidate counts. Built from the cheap initial load only (no specs/rates).
  // Seeded from every trade group in the taxonomy, so a group with no items
  // yet (a new one such as TIMBR) still lists, at zero, in the order and with
  // the label curated on the Taxonomies page.
  const groupSummaries = useMemo(() => {
    const m = new Map();
    (tradeGroups || []).forEach((tg) => {
      m.set(tg.code, { key: tg.code, tg, codes: [], candidates: 0 });
    });
    items.forEach((it) => {
      const { key, tg } = groupOf(it.itemCode, tradeGroupByItem);
      if (!m.has(key)) m.set(key, { key, tg, codes: [], candidates: 0 });
      const e = m.get(key);
      e.codes.push(it.itemCode);
      if ((versionsByItem.get(it.itemCode) || []).some((v) => v.status === 'draft')) e.candidates += 1;
    });
    return [...m.values()].sort(compareGroups);
  }, [items, tradeGroups, tradeGroupByItem, versionsByItem]);

  const candidateItemCount = useMemo(
    () => groupSummaries.reduce((n, g) => n + g.candidates, 0),
    [groupSummaries]
  );

  // Shape expected by TradeRail (count + candidate badge per trade group).
  const railGroups = useMemo(
    () => groupSummaries.map((g) => ({ key: g.key, tg: g.tg, count: g.codes.length, candidates: g.candidates })),
    [groupSummaries]
  );

  // Auto-select the first trade group once the library has loaded so the page
  // opens on real content rather than an empty pane.
  useEffect(() => {
    if (!loading && !searching && selectedKey == null && groupSummaries.length) {
      // Open on the first group holding items, not an empty one.
      const first = groupSummaries.find((g) => g.codes.length) || groupSummaries[0];
      setSelectedKey(first.key);
    }
  }, [loading, searching, selectedKey, groupSummaries]);

  // The items currently in view: the selected trade group, or — while
  // searching — every match across the whole library.
  const baseItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q) {
      return items.filter((i) => {
        const vers = versionsByItem.get(i.itemCode) || [];
        if (onlyCandidates && !vers.some((v) => v.status === 'draft')) return false;
        if (i.itemCode.toLowerCase().includes(q)) return true;
        return vers.some((v) => v.shortDescription?.toLowerCase().includes(q) || v.unit?.toLowerCase().includes(q));
      });
    }
    if (selectedKey == null) return [];
    return items.filter((i) => {
      if (keyOfItem(i.itemCode) !== selectedKey) return false;
      if (onlyCandidates && !(versionsByItem.get(i.itemCode) || []).some((v) => v.status === 'draft')) return false;
      return true;
    });
  }, [items, versionsByItem, query, onlyCandidates, selectedKey, keyOfItem]);

  // Lazily pull the heavy detail (long specs + rates) for whatever is in view.
  // Skipped for very broad searches — those rows load their detail on expand.
  useEffect(() => {
    if (loading) return;
    const codes = baseItems.map((i) => i.itemCode);
    if (searching && codes.length > SEARCH_AUTOLOAD_LIMIT) return;
    if (codes.length) loadGroupDetail(codes);
  }, [loading, searching, baseItems, loadGroupDetail]);

  const itemRows = useMemo(() => baseItems.map((item) => {
    const vers = [...(versionsByItem.get(item.itemCode) || [])].sort((a, b) => a.fullCode.localeCompare(b.fullCode));
    const activeVersions = vers.filter((v) => v.status === 'active');
    const latest = activeVersions[activeVersions.length - 1] || vers[vers.length - 1];
    const agg = rateAggByItem.get(item.itemCode);
    // A rated item reads its wording and unit from the version that priced the
    // rate shown, so a figure never sits beside another version's spec. Items
    // with no rate keep the latest active version.
    const shown = (agg?.latestVersionId && vers.find((v) => v.id === agg.latestVersionId)) || latest;
    return {
      item,
      versions: vers,
      description: shown?.shortDescription || '',
      unit: canonicalUnit(shown?.unit),
      versionCount: vers.length,
      // Split and Combine act on live versions only; an archived version left
      // by an earlier combine does not make an item "multi-version".
      liveVersionCount: vers.filter((v) => v.status !== 'deprecated').length,
      candidateCount: vers.filter((v) => v.status === 'draft').length,
      rateLoaded: rateAggByItem.has(item.itemCode),
      rateCount: agg?.count || 0,
      rateValue: agg?.latestVal ?? null,
      // Latest of the server's figure (covers rates, tags and deletions via the
      // audit log) and the item / version timestamps already in hand, so an edit
      // shows straight away and the column still works if the RPC is missing.
      modifiedAt: modifiedAtByItem
        ? Math.max(
          toMs(modifiedAtByItem.get(item.itemCode)) ?? -Infinity,
          toMs(item.updatedAt) ?? -Infinity,
          ...vers.map((v) => toMs(v.updatedAt) ?? -Infinity),
        )
        : null,
    };
  }), [baseItems, versionsByItem, rateAggByItem, modifiedAtByItem]);

  const { sorted, sort, toggle: toggleSort } = useSort(itemRows, {
    code: (r) => r.item.itemCode,
    description: (r) => r.description,
    unit: (r) => r.unit,
    versions: (r) => r.versionCount,
    rate: (r) => r.rateValue,
    modified: (r) => (Number.isFinite(r.modifiedAt) ? r.modifiedAt : null),
  }, undefined, { modified: 'desc' });

  // Resizable / collapsible / freezable / reorderable columns (persisted).
  const cols = useTableColumnLayout({ storageKey: LIB_LAYOUT_KEY, columns: LIB_COLUMNS });
  const totalWidth = useMemo(
    () => cols.orderedIds.reduce((sum, id) => sum + cols.layout[id].width, 0),
    [cols.orderedIds, cols.layout]
  );

  // While searching, results span multiple trades, so group them under their
  // trade-group heading. In single-trade mode there is exactly one group.
  const groups = useMemo(() => {
    const m = new Map();
    sorted.forEach((row) => {
      const { key, tg } = groupOf(row.item.itemCode, tradeGroupByItem);
      if (!m.has(key)) m.set(key, { key, tg, rows: [] });
      m.get(key).rows.push(row);
    });
    return [...m.values()].sort(compareGroups);
  }, [sorted, tradeGroupByItem]);

  const allExpanded = itemRows.length > 0 && itemRows.every((r) => expanded.has(r.item.itemCode));
  const toggleAllSpecs = () => setExpanded(allExpanded ? new Set() : new Set(itemRows.map((r) => r.item.itemCode)));
  // Coming back from Rates: this page unmounted while Rates was shown, so its
  // trade-group selection and open rows are gone. Reopen the item the rate link
  // was followed from, in its trade group, expanded, then scroll it into view
  // and flash it so the eye lands on it. Waits for the library to load, and
  // gives up quietly if the item is no longer there.
  const [flashCode, setFlashCode] = useState(null);
  useEffect(() => {
    if (!focusItemCode || loading) return;
    const consume = () => { if (onFocusConsumed) onFocusConsumed(); };
    if (!items.some((i) => i.itemCode === focusItemCode)) { consume(); return; }
    setQuery('');
    setSelectedKey(keyOfItem(focusItemCode));
    loadGroupDetail([focusItemCode]);
    setExpanded((prev) => new Set(prev).add(focusItemCode));
    setFlashCode(focusItemCode);
    consume();
  }, [focusItemCode, loading, items, keyOfItem, loadGroupDetail, onFocusConsumed]);
  useEffect(() => {
    if (!flashCode) return undefined;
    // After the trade group has rendered its rows.
    const raf = requestAnimationFrame(() => {
      const row = document.querySelector(`tr[data-item-code="${CSS.escape(flashCode)}"]`);
      if (row) row.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
    const t = setTimeout(() => setFlashCode(null), 2500);
    return () => { cancelAnimationFrame(raf); clearTimeout(t); };
  }, [flashCode]);

  const toggleItem = (code) => {
    loadGroupDetail([code]);                         // ensure specs/rates are present
    setExpanded((prev) => { const n = new Set(prev); if (n.has(code)) n.delete(code); else n.add(code); return n; });
  };

  // ── Select and move to Purge ─────────────────────────────────────────────
  // Only rows on screen can be picked, and never a row already in Purge:
  // nothing moves out of Purge in bulk, only one at a time through Edit.
  const selectableCodes = useMemo(
    () => ((readOnly || !canWrite) ? [] : sorted.map((r) => r.item.itemCode).filter((c) => !isPurgeCode(c))),
    [sorted, readOnly, canWrite]
  );
  const pickedCodes = useMemo(() => selectableCodes.filter((c) => picked.has(c)), [selectableCodes, picked]);
  const allPicked = selectableCodes.length > 0 && pickedCodes.length === selectableCodes.length;
  const togglePick = (code) => setPicked((prev) => {
    const next = new Set(prev); if (next.has(code)) next.delete(code); else next.add(code); return next;
  });
  const togglePickAll = () => setPicked(allPicked ? new Set() : new Set(selectableCodes));
  const confirmPurge = async () => {
    setPurgeBusy(true);
    setPurgeError(null);
    const res = await library.purgeItems(pickedCodes);
    setPurgeBusy(false);
    if (!res.success) { setPurgeError(res.error || 'Could not move the items to Purge.'); return; }
    setPurgeConfirm(false);
    setPicked(new Set());
    setExpanded((prev) => {
      const next = new Set(prev);
      res.moved.forEach((m) => { if (next.delete(m.from)) next.add(m.to); });
      return next;
    });
    setPurgeNotice(`Moved ${res.moved.length} item${res.moved.length === 1 ? '' : 's'} to Purge.`);
  };

  if (loading || switching) {
    return <div style={{ padding: '2rem', color: 'var(--brio-text-muted)' }}>{switching ? 'Loading version…' : 'Loading library…'}</div>;
  }
  if (error) {
    return (
      <BrioCard>
        <h3 style={{ marginTop: 0 }}>Couldn't load library</h3>
        <p style={{ color: 'var(--brio-text-muted)' }}>{error}</p>
        <BrioButton onClick={refresh}>Try again</BrioButton>
      </BrioCard>
    );
  }

  const showGroupHeadings = searching;             // single-trade view doesn't repeat the heading
  const colCount = cols.orderedIds.length;

  // Rate cell: distinguishes "not loaded yet" from "loaded, no rates yet".
  const rateCell = (row) => {
    if (!row.rateLoaded) return <span style={{ color: 'var(--brio-text-muted)' }}>·</span>;
    return (
      <button type="button" disabled={readOnly}
        onClick={() => !readOnly && onOpenRates
          && onOpenRates(row.item.itemCode, { itemCode: row.item.itemCode, description: row.description })}
        title={readOnly ? 'Rates are read-only in a saved version' : 'View / reconcile rates for this item'}
        style={{ background: 'transparent', border: 'none', cursor: readOnly ? 'not-allowed' : 'pointer', color: 'var(--brio-gold, #9F902A)', fontSize: '0.82rem', padding: 0, ...readOnlyStyle(readOnly) }}>
        {row.rateCount > 0 ? `${fmtNum(row.rateValue) ?? ''} · ${row.rateCount}` : 'add'}
      </button>
    );
  };

  // Content for one column's body cell (the layout — width/freeze/collapse —
  // is applied by the wrapper td below).
  const cellContent = (colId, row, isOpen) => {
    switch (colId) {
      case 'expand':
        return isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />;
      case 'code': {
        const pickable = !readOnly && canWrite && !isPurgeCode(row.item.itemCode);
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
            {pickable && (
              <input
                type="checkbox"
                checked={picked.has(row.item.itemCode)}
                onChange={() => togglePick(row.item.itemCode)}
                onClick={(e) => e.stopPropagation()}
                aria-label={`Select ${row.item.itemCode}`}
                style={{ cursor: 'pointer', margin: 0 }}
              />
            )}
            <code style={{ fontWeight: 600 }}>{row.item.itemCode}</code>
          </span>
        );
      }
      case 'description':
        return (
          <>
            {row.description || '—'}
            {row.candidateCount > 0 && <span style={{ marginLeft: 6, fontSize: '0.68rem', color: '#8a6d00' }}>({row.candidateCount} candidate{row.candidateCount === 1 ? '' : 's'})</span>}
          </>
        );
      case 'unit':
        return row.unit || '—';
      case 'versions':
        return row.versionCount;
      case 'rate':
        return rateCell(row);
      case 'modified':
        // Same size as the row's action buttons (+ Version etc.).
        return <span style={{ fontSize: '0.74rem' }}>{Number.isFinite(row.modifiedAt) ? fmtModified(row.modifiedAt) : '—'}</span>;
      case 'actions':
        return (
          <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-end' }}>
            {row.candidateCount > 0 && (
              <button type="button" disabled={readOnly || !canWrite}
                onClick={() => { loadGroupDetail([row.item.itemCode]); setReconcileTarget(row.item); }}
                title={readOnly ? 'Read-only — you are viewing a saved version' : (canWrite ? undefined : 'Reconciling is limited to admins and supervisors')}
                style={{ fontSize: '0.74rem', padding: '0.2rem 0.5rem', background: 'rgba(176,131,0,0.12)', border: '1px solid var(--brio-gold, #9F902A)', color: '#8a6d00', borderRadius: 4, cursor: 'pointer', ...readOnlyStyle(readOnly || !canWrite) }}>
                Reconcile {row.candidateCount}
              </button>
            )}
            <button type="button" disabled={readOnly || !canWrite}
              onClick={() => setMergeTarget(row)}
              title={readOnly ? 'Read-only — you are viewing a saved version'
                : (canWrite ? 'Merge with another item — choose which one stays active' : 'Merging items is limited to admins and supervisors')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.74rem', padding: '0.2rem 0.5rem', background: 'transparent', border: '1px solid var(--brio-border, #ccc)', borderRadius: 4, cursor: 'pointer', ...readOnlyStyle(readOnly || !canWrite) }}>
              <GitMerge size={12} /> Merge
            </button>
            <button type="button" disabled={readOnly}
              onClick={() => setDuplicateTarget(row)}
              title={readOnly ? 'Read-only — you are viewing a saved version' : 'Duplicate item — same content and classification, new code, no rates'}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.74rem', padding: '0.2rem 0.5rem', background: 'transparent', border: '1px solid var(--brio-border, #ccc)', borderRadius: 4, cursor: 'pointer', ...readOnlyStyle(readOnly) }}>
              <Copy size={12} /> Duplicate
            </button>
            <button type="button" disabled={readOnly || !canWrite}
              onClick={() => { loadGroupDetail([row.item.itemCode]); setEditorTarget(row.item.itemCode); }}
              title={readOnly
                ? 'Read-only — you are viewing a saved version'
                : (canWrite ? 'Edit item — content, status, classification & versions' : 'Editing an existing item is limited to admins and supervisors')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.74rem', padding: '0.2rem 0.5rem', background: 'transparent', border: '1px solid var(--brio-border, #ccc)', borderRadius: 4, cursor: 'pointer', ...readOnlyStyle(readOnly || !canWrite) }}>
              <Pencil size={12} /> Edit
            </button>
            {(() => {
              // Rate counts arrive with the per-trade detail load, so they may not
              // be known yet; only disable on a count we actually have. The server
              // refuses regardless, and says why.
              const rated = (rateAggByItem.get(row.item.itemCode)?.count || 0) > 0;
              const blocked = readOnly || !canWrite || rated;
              return (
                <button type="button" disabled={blocked}
                  onClick={() => setDeleteItemTarget(row)}
                  aria-label={`Delete ${row.item.itemCode}`}
                  title={readOnly ? 'Read-only — you are viewing a saved version'
                    : !canWrite ? 'Deleting a library item is limited to admins and supervisors'
                    : rated ? 'This item has rates against it and cannot be deleted — deprecate it instead'
                    : 'Delete item — only possible while nothing references it'}
                  style={{ display: 'inline-flex', alignItems: 'center', padding: '0.2rem 0.35rem', background: 'transparent', border: '1px solid var(--brio-border, #ccc)', borderRadius: 4, cursor: 'pointer', color: blocked ? undefined : '#a94442', ...readOnlyStyle(blocked) }}>
                  <Trash2 size={12} />
                </button>
              );
            })()}
          </div>
        );
      case 'versionActions': {
        if (row.liveVersionCount < 2) return null;
        const blocked = readOnly || !canWrite;
        const why = readOnly ? 'Read-only — you are viewing a saved version'
          : 'Splitting and combining versions is limited to admins and supervisors';
        const btn = { display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.74rem', padding: '0.2rem 0.5rem', background: 'transparent', border: '1px solid var(--brio-border, #ccc)', borderRadius: 4, cursor: 'pointer', ...readOnlyStyle(blocked) };
        return (
          <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'flex-start' }}>
            <button type="button" disabled={blocked} onClick={() => setSplitTarget(row.item.itemCode)}
              title={blocked ? why : 'Split — each version becomes its own item, with its rates'} style={btn}>
              <Split size={12} /> Split
            </button>
            <button type="button" disabled={blocked} onClick={() => setCombineTarget(row.item.itemCode)}
              title={blocked ? why : 'Combine — merge the versions you tick into one'} style={btn}>
              <Combine size={12} /> Combine
            </button>
          </div>
        );
      }
      default:
        return null;
    }
  };

  const renderItemRows = (rows) => rows.map((row) => {
    const isOpen = expanded.has(row.item.itemCode);
    const clickToggle = () => toggleItem(row.item.itemCode);
    return (
      <React.Fragment key={row.item.itemCode}>
        <tr
          className="qs-row"
          data-item-code={row.item.itemCode}
          style={{
            borderBottom: '1px solid var(--brio-border-subtle, #eee)',
            background: flashCode === row.item.itemCode ? 'var(--brio-gold-light, rgba(159,144,42,0.18))' : undefined,
            transition: 'background 0.6s ease',
          }}
        >
          {cols.orderedIds.map((colId) => {
            const meta = cols.layout[colId];
            const col = LIB_COLUMNS.find((c) => c.id === colId);
            // Most cells toggle the spec detail; the action cells do not.
            const clickable = colId !== 'actions' && colId !== 'versionActions';
            return (
              <td
                key={colId}
                onClick={clickable ? clickToggle : undefined}
                style={{
                  padding: meta.isCollapsed ? 0 : '0.5rem',
                  textAlign: col?.align || 'left',
                  cursor: clickable ? 'pointer' : 'default',
                  color: (colId === 'unit' || colId === 'versions' || colId === 'modified') ? 'var(--brio-text-muted)' : undefined,
                  overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                  ...colCellStyle(meta),
                }}
              >
                {meta.isCollapsed ? null : cellContent(colId, row, isOpen)}
              </td>
            );
          })}
        </tr>
        {isOpen && (
          <tr>
            <td colSpan={colCount} style={{ padding: '0.25rem 0.5rem 0.75rem' }}>
              {/* The three classifications, read only. They change only in the
                  Edit modal, because changing them can renumber the item and
                  that needs the preview and warnings the modal shows. Each
                  pair shrinks evenly (flex 1 1 0 + minWidth 0) so a long label
                  truncates instead of wrapping the row onto a second line. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.15rem 0 0.5rem', flexWrap: 'nowrap' }}>
                {(() => {
                  const code = row.item.itemCode;
                  const tg = tradeGroupByItem.get(code);
                  const subId = brioSubtradeNodeByItem.get(code);
                  const sub = (brioSubtrades || []).find((n) => n.id === subId);
                  const typeId = tradeTypeNodeByItem.get(code);
                  const typeOpts = tradeTypeOptionsFor(subId, typeId);
                  if (isPurgeCode(code)) {
                    return [
                      { key: 'trade-group', label: 'Trade group', value: null, empty: 'In Purge: click Edit to classify' },
                      { key: 'chapter', label: 'Chapter / sub-trade', value: null, empty: 'None' },
                      { key: 'trade-type', label: 'Trade type', value: null, empty: 'None' },
                    ];
                  }
                  return [
                    { key: 'trade-group', label: 'Trade group', value: tg ? `${tg.code} · ${tg.label}` : null, empty: 'Untagged' },
                    { key: 'chapter', label: 'Chapter / sub-trade', value: sub ? sub.label : null, empty: 'Unclassified' },
                    { key: 'trade-type', label: 'Trade type', value: typeId ? (typeOpts.find((o) => o.id === typeId)?.label || null) : null, empty: 'None' },
                  ];
                })().map((f) => (
                  <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flex: '1 1 0', minWidth: 0 }}>
                    <span style={{ fontSize: '0.66rem', textTransform: 'uppercase', letterSpacing: '0.03em', color: 'var(--brio-text-muted)', flexShrink: 0, whiteSpace: 'nowrap' }}>
                      {f.label}
                    </span>
                    <span
                      title={readOnly
                        ? 'Read-only — you are viewing a saved version'
                        : (canWrite ? 'Click Edit on this item to change its classification' : 'Classifying an existing item is limited to admins and supervisors')}
                      style={{
                        flex: '1 1 0', minWidth: 0,
                        fontSize: '0.8rem', padding: '0.2rem 0.4rem',
                        border: '1px solid var(--brio-border-subtle, #eee)', borderRadius: 4,
                        background: 'var(--brio-bg-subtle, #fafafa)',
                        color: f.value ? undefined : 'var(--brio-text-muted)',
                        overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
                      }}
                    >
                      {f.value || f.empty}
                    </span>
                  </div>
                ))}
              </div>
              {row.versions.map((v) => <VersionDetail key={v.id} version={v} />)}
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  });

  const selectedSummary = groupSummaries.find((g) => g.key === selectedKey);

  return (
    <div>
      {readOnly && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
          padding: '0.5rem 0.75rem', marginBottom: '0.75rem', borderRadius: 4,
          background: 'var(--brio-gold-light, rgba(159,144,42,0.12))',
          border: '1px solid var(--brio-gold, #9F902A)',
        }}>
          <Eye size={15} style={{ color: 'var(--brio-gold, #9F902A)', flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 200, fontSize: '0.84rem' }}>
            <strong>{archiveLabel(viewing.archive)}</strong>
            <span style={{ color: 'var(--brio-text-muted)' }}>
              {' · '}{viewing.archive.code}{' · saved '}{fmtDateTime(viewing.archive.snapshotAt)}
              {' · '}{viewing.archive.itemCount} items, {viewing.archive.versionCount} versions, {viewing.archive.rateCount} rates
              {' · read-only'}
            </span>
          </div>
          <BrioButton variant="secondary" onClick={returnToCurrent}>Back to current</BrioButton>
          <BrioButton variant="primary" onClick={() => setRestoreTarget(viewing.archive)}>Make current</BrioButton>
        </div>
      )}
      {archiveError && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.5rem',
          padding: '0.5rem 0.75rem', marginBottom: '0.75rem', borderRadius: 4,
          background: 'rgba(169,68,66,0.08)', border: '1px solid #a94442', color: '#a94442', fontSize: '0.84rem',
        }}>
          <AlertTriangle size={15} style={{ flexShrink: 0 }} /> {archiveError}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, minWidth: 240 }}>
          <BrioInput value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search all trades by code, description or unit…" style={{ flex: 1, maxWidth: 360 }} />
          {candidateItemCount > 0 && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.82rem', color: '#8a6d00', whiteSpace: 'nowrap', cursor: 'pointer' }}>
              <input type="checkbox" checked={onlyCandidates} onChange={(e) => setOnlyCandidates(e.target.checked)} />
              Needs reconciliation ({candidateItemCount})
            </label>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span style={{ fontSize: '0.85rem', color: 'var(--brio-text-muted)' }}>
            {items.length} item{items.length === 1 ? '' : 's'} · {groupSummaries.length} trade group{groupSummaries.length === 1 ? '' : 's'}
          </span>
          <BrioButton variant="secondary" onClick={toggleAllSpecs}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }} title={allExpanded ? 'Collapse all specs' : 'Expand all specs'}>
            {allExpanded ? <ChevronsDownUp size={15} /> : <ChevronsUpDown size={15} />}
            {allExpanded ? 'Collapse all' : 'Expand all'}
          </BrioButton>
          <BrioButton variant="secondary" onClick={cols.resetLayout}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }} title="Reset column layout (widths, order, collapsed & frozen)">
            <RotateCcw size={15} /> Reset columns
          </BrioButton>
          <BrioButton variant="secondary" onClick={refresh}>Refresh</BrioButton>
          <LibraryVersionsMenu
            archives={archivesHook.archives}
            viewingId={viewing?.archive.id || null}
            busy={archivesHook.busy}
            onAdd={() => setShowNewArchive(true)}
            onSelect={openArchive}
            onReturnToCurrent={returnToCurrent}
            onDelete={setDeleteTarget}
            canDelete={archivesHook.canDelete}
          />
          <BrioButton variant="primary" disabled={readOnly}
            title={readOnly ? 'Read-only — you are viewing a saved version' : undefined}
            style={readOnlyStyle(readOnly) || undefined}
            onClick={() => !readOnly && setShowCreateItem(true)}>+ New item</BrioButton>
        </div>
      </div>

      {items.length === 0 ? (
        <BrioCard>
          <BrioEmptyState>
            <div style={{ marginBottom: '0.75rem' }}>The library is empty.</div>
            <div style={{ fontSize: '0.85rem', marginBottom: '1rem', color: 'var(--brio-text-muted)' }}>
              Items use the format <code>TRADE.SUBTRADE.SEQUENCE</code>, e.g. <code>MA.EXT.0014</code>.
              Once seeded from sample BOQs the consultant can browse and version items here.
            </div>
            <BrioButton variant="primary" disabled={readOnly}
              style={readOnlyStyle(readOnly) || undefined}
              onClick={() => !readOnly && setShowCreateItem(true)}>Create the first item</BrioButton>
          </BrioEmptyState>
        </BrioCard>
      ) : (
        <div ref={containerRef} style={{ display: 'flex', alignItems: 'flex-start' }}>
          {/* Trade selector rail — drag the divider to resize, double-click to reset */}
          <TradeRail
            groups={railGroups}
            selectedKey={searching ? null : selectedKey}
            onSelect={(key) => { setQuery(''); setSelectedKey(key); }}
            storageKey={RAIL_WIDTH_KEY}
            containerRef={containerRef}
          />

          {/* Detail pane */}
          <div className="brio-analytics-surface" style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', marginBottom: '0.5rem', minHeight: 24 }}>
              <div style={{ fontWeight: 600 }}>
                {searching
                  ? <>Search results · {itemRows.length} item{itemRows.length === 1 ? '' : 's'}</>
                  : selectedSummary
                    ? <>{selectedSummary.tg ? selectedSummary.tg.label : 'Untagged'} · {itemRows.length} item{itemRows.length === 1 ? '' : 's'}</>
                    : 'Select a trade'}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                {detailLoading && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.78rem', color: 'var(--brio-text-muted)' }}>
                    <Loader2 size={13} className="brio-spin" /> Loading detail…
                  </span>
                )}
                {selectableCodes.length > 0 && (
                  <>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.8rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      <input
                        type="checkbox"
                        checked={allPicked}
                        ref={(el) => { if (el) el.indeterminate = pickedCodes.length > 0 && !allPicked; }}
                        onChange={togglePickAll}
                        style={{ margin: 0 }}
                      />
                      Select all ({selectableCodes.length})
                    </label>
                    <BrioButton
                      variant="secondary"
                      disabled={!pickedCodes.length}
                      onClick={() => { setPurgeError(null); setPurgeConfirm(true); }}
                      title="Move the ticked items to Purge: they are renumbered OO.OO.0001 onwards and lose their trade group, sub-trade and type"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', ...(pickedCodes.length ? null : { opacity: 0.45, cursor: 'not-allowed' }) }}
                    >
                      <Archive size={14} /> Move to Purge{pickedCodes.length ? ` (${pickedCodes.length})` : ''}
                    </BrioButton>
                  </>
                )}
              </div>
            </div>
            {purgeNotice && (
              <div style={{ fontSize: '0.8rem', padding: '0.4rem 0.6rem', marginBottom: '0.5rem', borderRadius: 4, background: 'rgba(46,125,50,0.10)', color: '#2e7d32' }}>
                {purgeNotice} Open <strong>{PURGE_LABEL}</strong> at the bottom of the trades list to classify them one at a time.
              </div>
            )}

            {itemRows.length === 0 ? (
              <BrioEmptyState>
                {searching ? 'No items match the current search.' : (selectedKey === PURGE_KEY ? 'Purge is empty.' : 'No items in this trade.')}
              </BrioEmptyState>
            ) : (
              <StickyScrollBox className="qs-table-scroll">
                <table className="qs-table" style={{ width: totalWidth, tableLayout: 'fixed', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', borderBottom: '2px solid var(--brio-border, #ddd)' }}>
                      {cols.orderedIds.map((colId) => {
                        const col = LIB_COLUMNS.find((c) => c.id === colId);
                        return (
                          <TableColHeader
                            key={colId}
                            col={col}
                            meta={cols.layout[colId]}
                            sortDir={sort.field === col.sortField ? sort.dir : null}
                            onSort={toggleSort}
                            onToggleCollapse={cols.toggleCollapse}
                            onToggleFreeze={cols.toggleFreeze}
                            startResize={cols.startResize}
                            onDragStart={cols.onDragStart}
                            onDrop={cols.onDrop}
                          />
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {showGroupHeadings
                      ? groups.map(({ key, tg, rows }) => (
                        <React.Fragment key={key}>
                          <tr style={{ background: 'var(--brio-surface-muted, #f7f7f5)' }}>
                            <td colSpan={colCount} style={{ padding: '0.4rem 0.5rem', fontWeight: 700, fontSize: '0.8rem' }}>
                              {tg ? <><code>{tg.code}</code> <span>{tg.label}</span></> : <span style={{ color: 'var(--brio-text-muted)' }}>Untagged</span>}
                              <span style={{ fontWeight: 400, color: 'var(--brio-text-muted)' }}> · {rows.length} item{rows.length === 1 ? '' : 's'}</span>
                            </td>
                          </tr>
                          {renderItemRows(rows)}
                        </React.Fragment>
                      ))
                      : renderItemRows(sorted)}
                  </tbody>
                </table>
              </StickyScrollBox>
            )}
          </div>
        </div>
      )}

      {/* Write modals are gated on !readOnly as well as their own state, so no
          path — a stale click, a keyboard shortcut — can edit a saved version. */}
      {!readOnly && showCreateItem && (
        <CreateLibraryItemModal
          onClose={() => setShowCreateItem(false)}
          onCreate={createItemWithFirstVersion}
          brioSubtrades={brioSubtrades}
          tradeGroups={tradeGroups}
          tradeTypes={tradeTypes}
          nextItemCode={nextItemCode}
          subtradeLinks={library.subtradeLinks}
        />
      )}
      {!readOnly && canWrite && reconcileTarget && (
        <ReconcileItemModal
          itemCode={reconcileTarget.itemCode}
          versions={versions}
          onReconcile={reconcileItem}
          onClose={() => setReconcileTarget(null)}
          onDone={refresh}
        />
      )}
      {!readOnly && duplicateTarget && (
        <DuplicateItemModal
          source={duplicateTarget}
          tradeTypes={tradeTypes}
          subtradeNodeId={brioSubtradeNodeByItem.get(duplicateTarget.item.itemCode)}
          sourceTradeTypeId={tradeTypeNodeByItem.get(duplicateTarget.item.itemCode)}
          generateCode={(typeId) => nextDuplicateCode(duplicateTarget.item.itemCode, typeId)}
          onDuplicate={duplicateItem}
          onClose={() => setDuplicateTarget(null)}
        />
      )}
      {!readOnly && canWrite && mergeTarget && (
        <MergeItemsModal
          source={mergeTarget}
          candidates={mergeCandidates}
          onMerge={mergeItems}
          onClose={(res) => {
            setMergeTarget(null);
            // Straight into the kept item, so both sets of versions are in view.
            if (res?.keptCode) setEditorTarget(res.keptCode);
          }}
        />
      )}
      {!readOnly && canWrite && splitTarget && (
        <SplitItemModal
          itemCode={splitTarget}
          tradeTypes={tradeTypes}
          loadCandidates={loadSplitCandidates}
          onSplit={splitItem}
          onClose={() => setSplitTarget(null)}
        />
      )}
      {!readOnly && canWrite && combineTarget && (
        <CombineVersionsModal
          itemCode={combineTarget}
          loadCandidates={loadSplitCandidates}
          onCombine={combineVersions}
          onClose={() => setCombineTarget(null)}
        />
      )}
      {!readOnly && canWrite && deleteItemTarget && (
        <DeleteItemModal
          source={deleteItemTarget}
          onDelete={() => deleteItem(deleteItemTarget.item.itemCode)}
          onClose={() => setDeleteItemTarget(null)}
        />
      )}
      {!readOnly && canWrite && editorTarget && (
        <ItemEditorModal
          library={library}
          itemCode={editorTarget}
          onClose={() => setEditorTarget(null)}
          onReclassified={({ oldCode, newCode, tradeGroup }) => {
            // Follow the item: keep the modal on its new code, keep its row
            // open, and show the trade group it now sits in.
            setEditorTarget(newCode);
            if (oldCode !== newCode) {
              setExpanded((prev) => {
                if (!prev.has(oldCode)) return prev;
                const next = new Set(prev); next.delete(oldCode); next.add(newCode); return next;
              });
            }
            // Sorting Purge one item at a time: stay in Purge, so the next item
            // is right there. Any other move follows the item to its group.
            if (!searching && !isPurgeCode(oldCode)) setSelectedKey(tradeGroup ? tradeGroup.code : UNTAGGED_KEY);
          }}
        />
      )}

      {!readOnly && canWrite && purgeConfirm && (
        <BrioModal title="Move to Purge" onClose={() => !purgeBusy && setPurgeConfirm(false)} width="sm" footer={
          <>
            <BrioButton variant="secondary" onClick={() => setPurgeConfirm(false)} disabled={purgeBusy}>Cancel</BrioButton>
            <BrioButton variant="primary" onClick={confirmPurge} disabled={purgeBusy}>
              {purgeBusy ? 'Moving…' : `Move ${pickedCodes.length} item${pickedCodes.length === 1 ? '' : 's'}`}
            </BrioButton>
          </>
        }>
          <BrioModalBody>
            {purgeError && (
              <div style={{ padding: '0.5rem 0.75rem', background: '#fdecec', color: '#a94442', borderRadius: 6, marginBottom: '0.75rem' }}>{purgeError}</div>
            )}
            <p style={{ marginTop: 0 }}>
              {pickedCodes.length} item{pickedCodes.length === 1 ? '' : 's'} will be renumbered <code>OO.OO.0001</code> onwards,
              in code order, and lose {pickedCodes.length === 1 ? 'its' : 'their'} trade group, chapter / subtrade and trade type.
              The numbers they hold now become free for the next item in each type.
            </p>
            <p>
              Project BOQs and issued estimates and tenders will show the Purge code until each item is classified
              again through <strong>Edit</strong>. Items cannot be moved out of Purge in bulk.
            </p>
            <p style={{ marginBottom: 0, fontSize: '0.8rem', color: 'var(--brio-text-muted)' }}>
              {pickedCodes.slice(0, 8).join(', ')}{pickedCodes.length > 8 ? ` and ${pickedCodes.length - 8} more` : ''}
            </p>
          </BrioModalBody>
        </BrioModal>
      )}

      {showNewArchive && (
        <NewArchiveModal busy={archivesHook.busy} onClose={() => setShowNewArchive(false)} onCreate={createArchive} />
      )}
      {restoreTarget && (
        <RestoreArchiveModal
          archive={restoreTarget}
          busy={archivesHook.busy}
          onClose={() => setRestoreTarget(null)}
          onConfirm={confirmRestore}
        />
      )}
      {restoreSummary && (
        <RestoreSummaryModal summary={restoreSummary} onClose={() => setRestoreSummary(null)} />
      )}
      {deleteTarget && archivesHook.canDelete && (
        <BrioModal title="Delete this version" onClose={() => setDeleteTarget(null)} width="sm" footer={
          <>
            <BrioButton variant="secondary" onClick={() => setDeleteTarget(null)}>Cancel</BrioButton>
            <BrioButton variant="primary" onClick={confirmDeleteArchive}>Delete</BrioButton>
          </>
        }>
          <BrioModalBody>
            <p style={{ marginTop: 0 }}>
              <strong>{archiveLabel(deleteTarget)}</strong> ({deleteTarget.code}) will be removed.
              The live library is not affected.
            </p>
          </BrioModalBody>
        </BrioModal>
      )}
    </div>
  );
}

export default LibraryPage;
